import { format0 } from 'utils/number';
import { fromDateParam } from 'utils/dates';
import type { SavedWellFilter } from 'types/type-curve-workspace-show';
import type {
  CategoricalFacet,
  RangeFacet,
  TcWellFilters,
  TcWellsFacets,
} from '../types';
import { TC_WELL_FILTER_CATEGORIES } from './utils';
import { definitionToTcWellFilters } from './saved-well-filter-definition';

// Human label for a flat-record key → its category label (with (from)/(to) for
// range min/max params).
export const findCategoryLabel = (paramKey: string): string => {
  for (const cat of TC_WELL_FILTER_CATEGORIES) {
    if (cat.param === paramKey) return cat.label;
    if (cat.minParam === paramKey) return `${cat.label} (from)`;
    if (cat.maxParam === paramKey) return `${cat.label} (to)`;
  }
  return paramKey;
};

// Resolve a picklist value id to its display name via the facet buckets.
export const resolveFacetName = (
  value: string,
  paramKey: string,
  facets: TcWellsFacets | null
): string => {
  if (!facets) return value;
  const cat = TC_WELL_FILTER_CATEGORIES.find(
    (c) =>
      c.param === paramKey || c.minParam === paramKey || c.maxParam === paramKey
  );
  if (!cat?.facetKey) return value;
  const facet = facets[cat.facetKey] as CategoricalFacet | undefined;
  const bucket = facet?.buckets?.find((b) => b.id === value);
  return bucket?.name ?? value;
};

export const getFacetRange = (
  category: (typeof TC_WELL_FILTER_CATEGORIES)[number],
  facets: TcWellsFacets | null
): { min: number | string | null; max: number | string | null } | null => {
  if (!facets || !category.facetKey) return null;
  const facet = facets[category.facetKey] as RangeFacet | undefined;
  if (!facet) return null;
  return { min: facet.min, max: facet.max };
};

export const formatRangeValue = (
  val: number | string | null | undefined,
  isNumeric: boolean
): string => {
  if (val === null || val === undefined) return '–';
  if (isNumeric) return format0(Number(val));
  return String(val);
};

// Group a flat `TcWellFilters` record into per-category chip descriptors
// `{ label, values }`, resolving value ids to display names and ranges to
// "min – max".
export const groupAppliedFilters = (
  filters: TcWellFilters,
  facets: TcWellsFacets | null
): Array<{ label: string; values: string[] }> => {
  const groups: Record<string, { label: string; values: string[] }> = {};
  const processedRangeCategories = new Set<string>();

  for (const [key, value] of Object.entries(filters)) {
    if (
      value === undefined ||
      value === '' ||
      (Array.isArray(value) && value.length === 0)
    )
      continue;

    const rangeCategory = TC_WELL_FILTER_CATEGORIES.find(
      (c) =>
        (c.type === 'numeric-range' || c.type === 'date-range') &&
        (c.minParam === key || c.maxParam === key)
    );

    if (rangeCategory) {
      if (processedRangeCategories.has(rangeCategory.id)) continue;
      processedRangeCategories.add(rangeCategory.id);

      const minVal = filters[rangeCategory.minParam!] as
        number | string | undefined;
      const maxVal = filters[rangeCategory.maxParam!] as
        number | string | undefined;

      if (rangeCategory.type === 'date-range') {
        // Format the filter's own bounds as dates, and do not fall back to the facet range: a
        // facet max is a full ISO timestamp, and a date filter usually sets only one bound, so
        // the chip reads "From 05/31/2025" rather than a raw "…T00:00:00.000Z" against a bound
        // the user never chose. Mirrors the modal's From / To / range wording.
        const min = minVal !== undefined ? fromDateParam(String(minVal)) : '';
        const max = maxVal !== undefined ? fromDateParam(String(maxVal)) : '';
        // Both bounds unreadable leaves nothing to say, so the category drops
        // out rather than rendering a chip with an empty side.
        if (min || max) {
          groups[rangeCategory.label] = {
            label: rangeCategory.label,
            values: [
              min && max
                ? `${min} – ${max}`
                : min
                  ? `From ${min}`
                  : `To ${max}`,
            ],
          };
        }
        continue;
      }

      const isNumeric = rangeCategory.type === 'numeric-range';
      const facetRange = getFacetRange(rangeCategory, facets);

      const displayMin =
        minVal !== undefined
          ? formatRangeValue(minVal, isNumeric)
          : formatRangeValue(facetRange?.min, isNumeric);
      const displayMax =
        maxVal !== undefined
          ? formatRangeValue(maxVal, isNumeric)
          : formatRangeValue(facetRange?.max, isNumeric);

      groups[rangeCategory.label] = {
        label: rangeCategory.label,
        values: [`${displayMin} – ${displayMax}`],
      };
      continue;
    }

    const label = findCategoryLabel(key);
    if (Array.isArray(value)) {
      groups[label] = {
        label,
        values: value.map((v) => resolveFacetName(String(v), key, facets)),
      };
    } else {
      groups[label] = { label, values: [String(value)] };
    }
  }

  return Object.values(groups);
};

// A one-line label for a persisted filter row, e.g. "Hole Direction: Horizontal".
export const labelForFilterRow = (
  row: SavedWellFilter,
  facets: TcWellsFacets | null
): string => {
  const groups = groupAppliedFilters(
    definitionToTcWellFilters(row.definition),
    facets
  );
  const parts = groups.map((g) => `${g.label}: ${g.values.join(', ')}`);
  return parts.join('; ') || row.name;
};
